import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock3, LogIn, LogOut } from "lucide-react";
import api from "@/api";
import { useAuth } from "@/AuthContext";

function elapsedText(status, nowMs) {
  const record = status?.record;
  if (!record?.check_in) return "";
  const sessions = record.sessions || [{ check_in: record.check_in, check_out: record.check_out }];
  const total = sessions.reduce((sum, session) => {
    if (!session.check_in) return sum;
    const start = new Date(session.check_in).getTime();
    const end = session.check_out ? new Date(session.check_out).getTime() : nowMs;
    return sum + Math.max(0, end - start);
  }, 0);
  const seconds = Math.floor(total / 1000);
  return `${String(Math.floor(seconds / 3600)).padStart(2, "0")}:${String(Math.floor((seconds % 3600) / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

export default function AttendanceWidget({ variant = "light" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const owner = user?.role === "super_admin" || user?.is_workspace_owner;

  useEffect(() => {
    if (!user || owner) return undefined;
    const refresh = () => api.get("/attendance/status").then((response) => setStatus(response.data)).catch(() => {});
    refresh();
    const timer = window.setInterval(() => { refresh(); setNowMs(Date.now()); }, 30000);
    window.addEventListener("attendance-updated", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("attendance-updated", refresh);
    };
  }, [user, owner]);

  const checkInTime = status?.record?.check_in
    ? new Date(status.record.check_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;
  const worked = elapsedText(status, nowMs);
  const canCheckIn = !!status && !status.checked_in && !status.exempt && !status.day_waived && status.requires_checkin;

  const toggle = useCallback(async () => {
    if (owner || status?.exempt || status?.day_waived) return;
    if (!status?.checked_in) {
      navigate("/check-in");
      return;
    }
    const warning = status.open_jobs_today ? `\n\nYou have ${status.open_jobs_today} open job(s) today.` : "";
    const confirmText = `Check out now?\nTotal hours worked: ${worked}${warning}`;
    if (!window.confirm(confirmText)) return;
    const note = window.prompt("Optional check-out note:", "");
    setBusy(true);
    try {
      await api.post("/attendance/checkout", { note: note || "" });
      const response = await api.get("/attendance/status");
      setStatus(response.data);
      window.dispatchEvent(new Event("attendance-updated"));
    } catch (error) {
      window.alert(error.response?.data?.detail || "Check-out failed");
    } finally {
      setBusy(false);
    }
  }, [navigate, owner, status, worked]);

  useEffect(() => {
    const runAttendanceAction = () => { void toggle(); };
    window.addEventListener("attendance-action", runAttendanceAction);
    return () => window.removeEventListener("attendance-action", runAttendanceAction);
  }, [toggle]);

  if (!user || owner || status?.exempt) return null;
  if (status?.day_waived) return <span className="text-xs text-muted-foreground">Attendance excused</span>;

  return (
    <div className="inline-flex min-w-0 items-center gap-2 text-xs" data-testid="attendance-header-widget">
      {status?.checked_in ? (
        <span className={`hidden sm:inline-flex items-center gap-1.5 whitespace-nowrap ${variant === "dark" ? "text-success" : "text-success-foreground dark:text-success"}`}>
          <span className="h-2 w-2 rounded-full bg-success" />
          <span>Checked in {checkInTime} | Working {worked}</span>
        </span>
      ) : status?.requires_checkin ? (
        <span className="hidden sm:inline text-warning-foreground dark:text-warning-foreground">Check-in required</span>
      ) : null}
      <button type="button" disabled={busy || (!status?.checked_in && !canCheckIn)} onClick={toggle} data-testid="attendance-header-action"
        className={`inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 font-medium hover:bg-accent disabled:opacity-50 ${variant === "dark" ? "border-border text-foreground hover:bg-surface-elevated" : ""}`}>
        {status?.checked_in ? <LogOut size={14} /> : canCheckIn ? <LogIn size={14} /> : <Clock3 size={14} />}
        {status?.checked_in ? "Check Out" : canCheckIn ? "Check In" : "Checked Out"}
      </button>
      {status?.checked_in && <Clock3 size={14} className="text-success sm:hidden" aria-label={`Working ${worked}`} />}
    </div>
  );
}
